import { OwnableCheckoutHandler } from '@Screens/Checkout/CheckoutHandlerClass';
import type { IValidateAuthedOrderResult } from '@Screens/Checkout/interfaces';
import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { action, computed, observable } from 'mobx';

import type {
  SavedAddressDTO,
  SavedCardDTO,
  UserOrderSummaryDTO,
} from '~gateways/ownable.dtos';
import container from '~ioc/ioc';
import AuthStore from '~stores/authentication';

type TCheckoutFeature =
  | 'AddNewAddress'
  | 'ChangeDeliveryAddressesDropdown'
  | 'ChangeItemQuantity'
  | 'ChangePaymentOptionsDropdown'
  | 'ChooseGuestOrLogin'
  | 'CollectDeliveryAddress'
  | 'CollectGuestEmail'
  | 'CollectGuestName'
  | 'CollectPaymentAddress'
  | 'CollectPaymentCardDetails'
  | 'ConfirmGuestDetailsButton'
  | 'ConfirmOrderButton'
  | 'SaveThisAddress'
  | 'SaveThisCard'
  | 'SetAsDefaultAddress'
  | 'SetAsDefaultCard'
  | 'ShowLoginFields'
  | 'ShowOrderConfirmation'
  | 'ShowOrderInfo';

type TCheckoutMode = 'authed' | 'guest';

type ICheckoutFeatureOptions = {
  availableForModes: TCheckoutMode[];
};

type RCheckoutConfig = Record<TCheckoutFeature, ICheckoutFeatureOptions>;

const checkoutConfig: RCheckoutConfig = {
  ChooseGuestOrLogin: {
    availableForModes: ['guest', 'authed'],
  },
  CollectGuestEmail: {
    availableForModes: ['guest'],
  },
  CollectGuestName: {
    availableForModes: ['guest'],
  },
  ConfirmGuestDetailsButton: {
    availableForModes: ['guest'],
  },
  ShowLoginFields: {
    availableForModes: ['guest'],
  },
  CollectDeliveryAddress: {
    availableForModes: ['guest', 'authed'],
  },
  CollectPaymentAddress: {
    availableForModes: ['guest'],
  },
  ChangePaymentOptionsDropdown: {
    availableForModes: ['authed'],
  },
  ChangeDeliveryAddressesDropdown: {
    availableForModes: ['authed'],
  },
  ChangeItemQuantity: {
    availableForModes: [], // Currently only supports buying 1 item
  },
  ShowOrderInfo: {
    availableForModes: ['authed', 'guest'],
  },
  ShowOrderConfirmation: {
    availableForModes: ['authed', 'guest'],
  },
  SaveThisAddress: {
    availableForModes: ['authed'],
  },
  SaveThisCard: {
    availableForModes: ['authed'],
  },
  SetAsDefaultAddress: {
    availableForModes: ['authed'],
  },
  SetAsDefaultCard: {
    availableForModes: ['authed'],
  },
  ConfirmOrderButton: {
    availableForModes: ['authed', 'guest'],
  },
  CollectPaymentCardDetails: {
    availableForModes: ['guest'],
  },
  AddNewAddress: {
    availableForModes: ['authed'],
  },
};

@injectable()
class CheckoutStore {
  @inject(AuthStore)
  private AuthStore!: AuthStore;
  @observable
  public CheckoutHandlerSingleton = new OwnableCheckoutHandler({
    onError: () => {
      // do nothing
    },
  });

  @observable useDeliveryAddressForBilling: boolean = false;
  @observable uiShowBuyFlow: boolean = false;
  @observable selectedAddress: SavedAddressDTO | undefined = undefined;
  @observable addresses: SavedAddressDTO[] | undefined = undefined;
  @observable temporaryAddressID = new Date().getTime();
  @observable addressMenuVisible: boolean = false;
  @observable addressFormVisible: boolean = false;

  @observable selectedCard: SavedCardDTO | undefined = undefined;
  @observable paymentIntentId: string | undefined = undefined;
  @observable savedCards: SavedCardDTO[] | undefined = undefined;

  @observable currentAvailableCheckoutFeatures: TCheckoutFeature[] = [];
  @observable currentCheckoutMode: TCheckoutMode = 'guest';
  @observable guestCheckoutName: string = '';
  @observable guestCheckoutEmail: string = '';

  @observable checkoutLoginEmail: string = '';
  @observable checkoutLoginPassword: string = '';

  @observable checkoutOrderId: number | undefined = undefined;
  @observable checkoutOrderToken: string | undefined = undefined;
  @observable cardMenuVisible: boolean = false;
  @observable addCardFormVisible: boolean = false;

  @observable authedUserSelectedPaymentMethod: SavedCardDTO | undefined =
    undefined;
  @observable authedUserSelectedDeliveryAddress: SavedAddressDTO | undefined =
    undefined;
  @observable authedUserSelectedDeliveryAddressId: number | undefined =
    undefined;
  @observable authedUserSaveNewAddress: boolean = false;
  @observable authedUserSetNewAddressAsDefault: boolean = false;

  @observable cardDetailsComplete: boolean = false;

  @observable submitOrderValidationResult: IValidateAuthedOrderResult | null =
    null;
  @observable placeOrderButtonText: 'Place order' | 'Retry order' =
    'Place order';

  @observable orderErrorMessage: string | undefined = '';

  @observable orderSummary: UserOrderSummaryDTO | null = null;
  @observable quantity: number = 1;
  @observable guestCheckout: boolean = false;
  @observable guestUser: { name: string; email: string } | null = null;
  @observable orderFlowStep: 0 | 1 | 2 = 0;
  @observable identityFlowStep: 0 | 1 | 2 = 0;
  @observable shouldSaveCard = false;
  @observable shouldSaveCardDefault = false;
  @observable cardDisplayName = '';

  // Used for https://app.clickup.com/t/m782wn
  // Allows us to check if details should be cleared so user can check out further items (and have their details auto-refilled)
  // Will be set to true once user has completed an order as guest
  // Will be set to false if user logs in
  @observable guestCheckoutUserHasCompletedOrder: boolean = false;

  @observable checkoutDeliveryAddress: Omit<
    SavedAddressDTO,
    'id' | 'isDefault'
  > = {
    county: '',
    displayName: '',
    phone: '',
    postcode: '',
    recipientName: '',
    street: '',
    townOrCity: '',
  };

  @observable checkoutBillingAddress: Omit<
    SavedAddressDTO,
    'id' | 'isDefault'
  > = {
    county: '',
    displayName: '',
    phone: '',
    postcode: '',
    recipientName: '',
    street: '',
    townOrCity: '',
  };

  @action setCheckoutDeliveryAddress = (
    newState: Omit<SavedAddressDTO, 'id' | 'isDefault'> = {
      county: '',
      displayName: '',
      phone: '',
      postcode: '',
      recipientName: '',
      street: '',
      townOrCity: '',
    }
  ) => {
    this.checkoutDeliveryAddress = newState;
  };

  @action setCheckoutBillingAddress = (
    newState: Omit<SavedAddressDTO, 'id' | 'isDefault'> = {
      county: '',
      displayName: '',
      phone: '',
      postcode: '',
      recipientName: '',
      street: '',
      townOrCity: '',
    }
  ) => {
    this.checkoutBillingAddress = newState;
  };

  @action setShouldSaveCard = (val: boolean) => (this.shouldSaveCard = val);

  @action setShouldSaveCardDefault = (val: boolean) =>
    (this.shouldSaveCardDefault = val);

  @action setCardDisplayName = (val: string) => (this.cardDisplayName = val);

  @action clearForms = () => {
    this.orderSummary = null;
    this.quantity = 1;
    this.guestCheckout = false;
    this.guestUser = null;
    this.orderFlowStep = 0;
    this.identityFlowStep = 0;
    this.setCheckoutDeliveryAddress();
    this.setCheckoutBillingAddress();
    this.setShouldSaveCard(false);
    this.setShouldSaveCardDefault(false);
    this.setCardDisplayName('');
    this.setAuthedUserSaveNewAddress(false);
    this.setAuthedUserSetNewAddressAsDefault(false);
    this.setUseDeliveryAddressForBilling(false);
    this.setGuestCheckoutName();
    this.setGuestCheckoutEmail();
    this.CheckoutHandlerSingleton.resetSingletonStatus();
  };

  @action setOrderErrorMessage = (newState: string | undefined = undefined) => {
    this.orderErrorMessage = newState;
  };

  @action setPlaceOrderButtonText = (
    newState: 'Place order' | 'Retry order' = 'Place order'
  ) => {
    this.placeOrderButtonText = newState;
  };

  @action setSubmitOrderValidationResult = (
    newState: IValidateAuthedOrderResult | null = null
  ) => {
    this.submitOrderValidationResult = newState;
  };

  @action setGuestCheckoutUserHasCompletedOrder = (
    newState: boolean = !this.guestCheckoutUserHasCompletedOrder
  ) => {
    this.guestCheckoutUserHasCompletedOrder = newState;
  };

  @action setCardDetailsComplete = (
    newState: boolean = !this.cardDetailsComplete
  ) => {
    this.cardDetailsComplete = newState;
  };

  @action setAuthedUserSelectedDeliveryAddress = (
    newState: SavedAddressDTO | undefined
  ) => {
    this.authedUserSelectedDeliveryAddress = newState;
  };

  @action setAuthedUserSaveNewAddress = (
    newState: boolean = !this.authedUserSaveNewAddress
  ) => {
    this.authedUserSaveNewAddress = newState;
  };

  @action setAuthedUserSetNewAddressAsDefault = (
    newState: boolean = !this.authedUserSetNewAddressAsDefault
  ) => {
    this.authedUserSetNewAddressAsDefault = newState;
  };

  @action openCheckoutDetailsFlow = () => {
    this.setCurrentCheckoutAvailableFeatures([
      'ChangePaymentOptionsDropdown',
      'ChangeDeliveryAddressesDropdown',
      'ShowOrderInfo',
      'ConfirmOrderButton',
    ]);
  };

  @action requestNewCheckoutCardDetails = () => {
    this.setCurrentCheckoutAvailableFeatures([
      ...this.currentAvailableCheckoutFeatures,
      'CollectPaymentCardDetails',
    ]);
  };

  @action requestNewCheckoutDeliveryAddress = () => {
    this.setCurrentCheckoutAvailableFeatures([
      ...this.currentAvailableCheckoutFeatures,
      'CollectDeliveryAddress',
    ]);
  };

  @action openCheckoutCompleteFlow = () => {
    this.setCurrentCheckoutAvailableFeatures(['ShowOrderConfirmation']);
  };

  @observable isFeatureAvailableForCheckout = (feature: TCheckoutFeature) =>
    this.currentAvailableCheckoutFeatures.includes(feature) &&
    checkoutConfig[feature].availableForModes.includes(
      this.currentCheckoutMode
    );

  @computed get showIdentityStep() {
    return this.currentAvailableCheckoutFeatures.includes('ChooseGuestOrLogin');
  }

  @action setAuthedUserSelectedDeliveryAddressId = (
    newState: number | undefined = undefined
  ) => {
    this.authedUserSelectedDeliveryAddressId = newState;
  };

  @action setAuthedUserSelectedPaymentMethod = (
    newState: SavedCardDTO | undefined = undefined
  ) => {
    this.authedUserSelectedPaymentMethod = newState;
  };

  @action setCurrentCheckoutMode = (newState: TCheckoutMode = 'guest') => {
    this.currentCheckoutMode = newState;
  };

  @action setCheckoutLoginEmail = (newState: string = '') => {
    this.checkoutLoginEmail = newState;
  };

  @action setCheckoutLoginPassword = (newState: string = '') => {
    this.checkoutLoginPassword = newState;
  };

  @action setCheckoutOrderToken = (newState: string = '') => {
    this.checkoutOrderToken = newState;
  };

  @action setCheckoutDeliveryAddressField = (
    field: keyof Omit<SavedAddressDTO, 'id' | 'isDefault'>,
    newState: string
  ) => {
    this.checkoutDeliveryAddress[field] = newState;
  };

  @action setUseDeliveryAddressForBilling = (setting: boolean) => {
    this.useDeliveryAddressForBilling = setting;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { recipientName, ...rest } = this.checkoutDeliveryAddress;
    this.checkoutBillingAddress = {
      ...rest,
      recipientName: this.checkoutBillingAddress.recipientName,
    };
  };

  @action setCheckoutBillingAddressField = (
    field: keyof Omit<SavedAddressDTO, 'id' | 'isDefault'>,
    newState: string
  ) => {
    this.checkoutBillingAddress[field] = newState;
  };

  @action setGuestCheckoutName = (newState: string = '') => {
    this.guestCheckoutName = newState;
  };

  @action setGuestCheckoutEmail = (newState: string = '') => {
    this.guestCheckoutEmail = newState;
  };

  @action setCurrentCheckoutAvailableFeatures = (
    features: TCheckoutFeature[] = []
  ) => {
    this.currentAvailableCheckoutFeatures = features;
  };

  @action public resetCheckoutFlow = () => {
    this.setCurrentCheckoutAvailableFeatures([]);
    this.setSubmitOrderValidationResult(null);
    this.setUiShowBuyFlow(false);
    this.orderSummary = null;
    this.guestCheckout = false;
    this.quantity = 1;
    this.orderFlowStep = 0;
    this.identityFlowStep = 0;
    this.guestUser = null;
    this.checkoutOrderId = undefined;
    this.setPlaceOrderButtonText('Place order');
    this.CheckoutHandlerSingleton.resetSingletonStatus();
    this._onCancelCheckoutButton();
  };

  @action public readonly setUiShowBuyFlow = (
    newState: boolean = !this.uiShowBuyFlow
  ) => {
    this.uiShowBuyFlow = newState;
  };

  @action
  public readonly checkBuyFlow = async () => {
    if (this.uiShowBuyFlow) {
      this.resetCheckoutFlow();
    }
  };

  @action setSelectedAddress = (
    address: SavedAddressDTO | undefined = undefined
  ) => {
    this.selectedAddress = address;
  };

  @observable public localAddress: SavedAddressDTO = {
    id: this.temporaryAddressID,
    isDefault: false,
    county: '',
    displayName: '',
    phone: '',
    postcode: '',
    recipientName: '',
    street: '',
    townOrCity: '',
  };

  @computed get defaultAddress() {
    if (this.addresses) {
      return (
        this.addresses.find(({ isDefault }) => isDefault) ??
        this.addresses?.[0] ??
        undefined
      );
    }
    return undefined;
  }

  @computed get defaultSavedCard() {
    if (this.savedCards) {
      return (
        this.savedCards.find(({ isDefault }) => isDefault) ??
        this.savedCards?.[0]
      );
    }
    return undefined;
  }

  // --------------------------------

  @action public readonly _onCancelCheckoutButton = () => {
    this.clearForms();
    this.setUiShowBuyFlow(false);
    this.setCheckoutMode('guest');
    this.setCurrentCheckoutAvailableFeatures(['ChooseGuestOrLogin']);
    this.addCardFormVisible = false;
    this.addressFormVisible = false;
    this.addressMenuVisible = false;
    this.cardMenuVisible = false;
  };

  @action setCheckoutMode = (newState: 'authed' | 'guest' = 'guest') => {
    this.currentCheckoutMode = newState;
  };

  @action public readonly _onUserChooseToLoginButton = async () => {
    // this.CheckoutStore.clearForms();
    this.setCurrentCheckoutAvailableFeatures(['ShowLoginFields']);
  };

  // -------------------------------
}

export const useCheckoutStore = () =>
  useClassStore<CheckoutStore>(container.get(CheckoutStore));

export default CheckoutStore;
